import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { Button } from "@/components/ui/button";
import { InlineLoader } from "@/components/ui/pulsing-loader";
import { CalendarDays, History, Info, Receipt } from "lucide-react";
import { MinisterReceiptsTab } from "@/features/receipts/components/MinisterReceiptsTab";
import { MinisterEventsTab } from "@/features/events/components/MinisterEventsTab";
import { AffiliationReceiptFlow } from "@/features/receipts/components/AffiliationReceiptFlow";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { MinisterForm } from "../components/organisms/MinisterForm";
import { InactivateMinisterDialog } from "../components/molecules/InactivateMinisterDialog";
import { ReactivateMinisterDialog } from "../components/molecules/ReactivateMinisterDialog";
import { DegreeChangeDialog } from "../components/molecules/DegreeChangeDialog";
import { TransferDialog } from "../components/molecules/TransferDialog";
import { MinisterHistoryTimeline } from "../components/molecules/MinisterHistoryTimeline";
import { useMinisterForm } from "../hooks/useMinisterForm";
import { useMinister } from "../hooks/useMinister";
import { useUserProfile } from "../hooks/useUserProfile";
import { usePermissions } from "@/hooks/usePermissions";
import { useInvalidateAllMinisters } from "../hooks/useAllMinisters";
import type { Minister } from "@/models/minister";
import type { District } from "@/models/district";
import type { Region } from "@/models/region";
import type { Degree } from "@/models/degree";
import type { MinisterFormData, InactiveReason } from "../types";
import {
  getMinisterById,
  getDegreesList,
  getAllDistrictsAndRegions,
  getInactiveReasons,
  inactivateMinister,
  reactivateMinister,
  changeDegree,
  transferMinister,
} from "@/data/ministerRepository";
import { useToast } from "@/hooks/use-toast";

/**
 * Page: Minister detail/edit page
 * Shows minister form in edit mode with save/cancel/back actions
 */
export function MinisterDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useUserProfile();
  const { can } = usePermissions();
  const { toast } = useToast();

  // State for data loading
  const [minister, setMinister] = useState<Minister | null>(null);
  // Permissions are checked against the minister's own district / region
  const target = { districtId: minister?.district_id, regionId: minister?.region_id };
  const canEdit = !!minister && can('minister.edit', target);
  const canInactivate = !!minister && can('minister.inactivate', target);
  const canChangeDegree = !!minister && can('minister.change_degree', target);
  const canViewReceipts = can('receipt.view');
  const canIssueReceipt = can('receipt.issue');
  const [districts, setDistricts] = useState<District[]>([]);
  const [regions, setRegions] = useState<Region[]>([]);
  const [degrees, setDegrees] = useState<Degree[]>([]);
  const [inactiveReasons, setInactiveReasons] = useState<InactiveReason[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [initialFormData, setInitialFormData] = useState<MinisterFormData | null>(null);
  const [hasFormChanged, setHasFormChanged] = useState(false);
  const [showCancelDialog, setShowCancelDialog] = useState(false);
  const [activeTab, setActiveTab] = useState<'info' | 'history' | 'receipts' | 'events'>('info');
  const [showAffiliationReceipt, setShowAffiliationReceipt] = useState(false);
  const [showInactivateDialog, setShowInactivateDialog] = useState(false);
  const [showReactivateDialog, setShowReactivateDialog] = useState(false);
  const [showDegreeChangeDialog, setShowDegreeChangeDialog] = useState(false);
  const [showTransferDialog, setShowTransferDialog] = useState(false);
  const [historyKey, setHistoryKey] = useState(0);

  // CRUD operations
  const { updateMinister, isSubmitting } = useMinister();
  const invalidateAllMinisters = useInvalidateAllMinisters();

  // Form management
  const {
    formData,
    errors,
    touched,
    photoFile,
    processedPhotoFile,
    photoPreview,
    updateField,
    markFieldTouched,
    validate,
    handlePhotoSelect,
    shouldDeletePhoto,
  } = useMinisterForm(minister || undefined);

  // Load minister data
  useEffect(() => {
    const loadData = async () => {
      try {
        setIsLoading(true);

        // Load districts with regions, degrees, and inactive reasons
        const [districtsData, degreesData, reasonsData] = await Promise.all([
          getAllDistrictsAndRegions(),
          getDegreesList(),
          getInactiveReasons(),
        ]);
        setInactiveReasons(reasonsData);

        setDistricts(districtsData);
        setDegrees(degreesData);

        // Extract all regions from districts
        const allRegions: Region[] = districtsData.flatMap(d => d.regions || []);
        setRegions(allRegions);

        // Load minister by ID (efficient single query)
        const ministerData = await getMinisterById(id!);

        if (!ministerData) {
          toast({
            variant: "destructive",
            title: "Ministro no encontrado",
            description: "El ministro solicitado no existe en la base de datos.",
          });
          navigate("/ministers");
          return;
        }

        setMinister(ministerData);

        // Populate form with minister data using updateField from the hook
        const fields: (keyof typeof formData)[] = [
          'adb_code', 'first_name', 'last_name', 'second_last_name', 'marriage_last_name',
          'dni', 'email', 'cell_phone', 'office_phone', 'position',
          'district_id', 'region_id', 'degree_id', 'address', 'address_city', 'address_zone'
        ];

        fields.forEach(field => {
          const value = ministerData[field as keyof Minister];
          if (typeof value === 'string' || value === null || value === undefined) {
            updateField(field, value || "");
          }
        });

        // Handle is_active separately (boolean field)
        updateField('is_active', ministerData.is_active ?? true);

        // Store initial form data for change detection
        const initialData: MinisterFormData = {
          adb_code: ministerData.adb_code || "",
          first_name: ministerData.first_name || "",
          last_name: ministerData.last_name || "",
          second_last_name: ministerData.second_last_name || "",
          marriage_last_name: ministerData.marriage_last_name || "",
          dni: ministerData.dni || "",
          email: ministerData.email || "",
          cell_phone: ministerData.cell_phone || "",
          office_phone: ministerData.office_phone || "",
          position: ministerData.position || "",
          district_id: ministerData.district_id || "",
          region_id: ministerData.region_id || "",
          degree_id: ministerData.degree_id || "",
          address: ministerData.address || "",
          address_city: ministerData.address_city || "",
          address_zone: ministerData.address_zone || "",
          photo_url: "",
          is_active: ministerData.is_active ?? true,
        };
        setInitialFormData(initialData);
      } catch (error) {
        console.error("Error loading minister data:", error);
        toast({
          variant: "destructive",
          title: "Error al cargar datos",
          description: "No se pudo cargar la información del ministro.",
        });
        navigate(-1);
      } finally {
        setIsLoading(false);
      }
    };

    if (id) {
      loadData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, navigate]);

  // Detect form changes (compare current form data with initial data)
  useEffect(() => {
    if (!initialFormData) {
      setHasFormChanged(false);
      return;
    }

    // Check if photo changed
    const photoChanged = photoFile !== null || shouldDeletePhoto;

    // Check if any field changed
    const fieldsChanged = Object.keys(initialFormData).some(key => {
      const fieldKey = key as keyof MinisterFormData;
      return formData[fieldKey] !== initialFormData[fieldKey];
    });

    setHasFormChanged(photoChanged || fieldsChanged);
  }, [formData, photoFile, shouldDeletePhoto, initialFormData]);

  const handleSubmit = async () => {
    if (!minister || !canEdit || !user) return;

    const isValid = await validate();
    if (!isValid) {
      toast({
        variant: "destructive",
        title: "Errores en el formulario",
        description: "Por favor corrija los errores marcados antes de continuar.",
      });
      return;
    }

    try {
      const result = await updateMinister(
        minister.id,
        formData,
        user.id,
        processedPhotoFile,
        photoFile,
        minister,
        shouldDeletePhoto
      );

      if (result?.minister) {
        toast({
          title: "Ministro actualizado",
          description: "Los cambios se han guardado correctamente.",
        });

        if (result.photoError) {
          toast({
            variant: "destructive",
            title: "Error al subir foto",
            description: result.photoError + " - Los datos del ministro se guardaron correctamente.",
          });
        }

        navigate(-1);
      } else {
        throw new Error("Failed to update minister");
      }
    } catch (error) {
      console.error("Error updating minister:", error);
      toast({
        variant: "destructive",
        title: "Error al actualizar",
        description: "No se pudo guardar la información del ministro.",
      });
    }
  };

  const handleDegreeChangeConfirm = async (newDegreeId: string, eventDate: string, notes?: string) => {
    if (!minister || !user) return;
    try {
      await changeDegree(minister.id, minister.degree_id ?? null, newDegreeId, eventDate, user.id, notes);
      const newDegree = degrees.find(d => d.id === newDegreeId);
      setMinister(prev => prev ? {
        ...prev,
        degree_id: newDegreeId,
        degree: newDegree ? { id: newDegree.id, name: newDegree.name } : prev.degree,
      } : prev);
      updateField('degree_id', newDegreeId);
      setInitialFormData(prev => prev ? { ...prev, degree_id: newDegreeId } : prev);
      setHistoryKey(k => k + 1);
      invalidateAllMinisters();
      toast({ title: "Grado actualizado", description: "El cambio de grado se registró correctamente." });
    } catch {
      toast({ variant: "destructive", title: "Error", description: "No se pudo registrar el cambio de grado." });
    }
    setShowDegreeChangeDialog(false);
  };

  const handleTransferConfirm = async (newDistrictId: string, newRegionId: string, eventDate: string, notes?: string) => {
    if (!minister || !user) return;
    try {
      await transferMinister(
        minister.id,
        minister.district_id ?? null,
        newDistrictId,
        minister.region_id ?? null,
        newRegionId,
        eventDate,
        user.id,
        notes
      );
      const newDistrict = districts.find(d => d.id === newDistrictId);
      const newRegion = regions.find(r => r.id === newRegionId);
      setMinister(prev => prev ? {
        ...prev,
        district_id: newDistrictId,
        region_id: newRegionId,
        district: newDistrict ? { id: newDistrict.id, name: newDistrict.name, code: newDistrict.code } : prev.district,
        region: newRegion ? { id: newRegion.id, name: newRegion.name, code: (newRegion as Region & { code?: string }).code ?? '' } : prev.region,
      } : prev);
      updateField('district_id', newDistrictId);
      updateField('region_id', newRegionId);
      setInitialFormData(prev => prev ? { ...prev, district_id: newDistrictId, region_id: newRegionId } : prev);
      setHistoryKey(k => k + 1);
      invalidateAllMinisters();
      toast({ title: "Traslado registrado", description: "El traslado se registró correctamente." });
    } catch {
      toast({ variant: "destructive", title: "Error", description: "No se pudo registrar el traslado." });
    }
    setShowTransferDialog(false);
  };

  const handleInactivate = async (reasonCode: string, eventDate: string, notes?: string) => {
    if (!minister || !user) return;
    try {
      await inactivateMinister(minister.id, reasonCode, eventDate, user.id, notes);
      setMinister(prev => prev ? { ...prev, is_active: false, inactive_reason_code: reasonCode, inactive_at: eventDate } : prev);
      updateField('is_active', false);
      setInitialFormData(prev => prev ? { ...prev, is_active: false } : prev);
      setHistoryKey(k => k + 1);
      invalidateAllMinisters();
      toast({ title: "Ministro inactivado", description: "El cambio de estado se registró correctamente." });
    } catch {
      toast({ variant: "destructive", title: "Error", description: "No se pudo inactivar el ministro." });
    }
    setShowInactivateDialog(false);
  };

  const handleReactivate = async (eventDate: string, notes?: string) => {
    if (!minister || !user) return;
    try {
      await reactivateMinister(minister.id, eventDate, user.id, notes);
      setMinister(prev => prev ? { ...prev, is_active: true, inactive_reason_code: null, inactive_at: null } : prev);
      updateField('is_active', true);
      setInitialFormData(prev => prev ? { ...prev, is_active: true } : prev);
      setHistoryKey(k => k + 1);
      invalidateAllMinisters();
      toast({ title: "Ministro reactivado", description: "El cambio de estado se registró correctamente." });
    } catch {
      toast({ variant: "destructive", title: "Error", description: "No se pudo reactivar el ministro." });
    }
    setShowReactivateDialog(false);
  };

  const handleCancel = () => {
    if (hasFormChanged) {
      setShowCancelDialog(true);
    } else {
      navigate(-1);
    }
  };

  const handleConfirmCancel = () => {
    setShowCancelDialog(false);
    navigate(-1);
  };

  const handleBack = () => {
    navigate(-1);
  };

  if (isLoading) {
    return (
      <div className="container mx-auto py-6">
        <div className="flex min-h-[400px] items-center justify-center">
          <InlineLoader message="Cargando información del ministro..." />
        </div>
      </div>
    );
  }

  if (!minister) {
    return null;
  }

  const handleStatusToggle = (checked: boolean) => {
    if (checked) {
      setShowReactivateDialog(true);
    } else {
      setShowInactivateDialog(true);
    }
  };

  return (
    <div className="container mx-auto max-w-4xl py-6">
      {/* Header */}
      <div className="mb-6">
        <Button variant="ghost" onClick={handleBack} className="-ml-4 mb-4">
          <ArrowLeft className="mr-2 size-4" />
          Volver a la lista
        </Button>
        <h1 className="text-3xl font-bold">Editar Ministro</h1>
        <p className="text-muted-foreground">
          Modifica la información del ministro {minister.full_name}
        </p>
      </div>

      {/* Tabs */}
      <div className="mb-6 border-b">
        <nav className="flex gap-1">
          <button
            onClick={() => setActiveTab('info')}
            className={`flex items-center gap-2 border-b-2 px-4 py-3 text-sm font-medium transition-colors hover:border-primary/50 hover:bg-primary/10 hover:text-primary ${
              activeTab === 'info'
                ? 'border-primary text-primary'
                : 'border-transparent text-muted-foreground'
            }`}
          >
            <Info className="size-4" />
            Información
          </button>
          <button
            onClick={() => setActiveTab('history')}
            className={`flex items-center gap-2 border-b-2 px-4 py-3 text-sm font-medium transition-colors hover:border-primary/50 hover:bg-primary/10 hover:text-primary ${
              activeTab === 'history'
                ? 'border-primary text-primary'
                : 'border-transparent text-muted-foreground'
            }`}
          >
            <History className="size-4" />
            Historial
          </button>
          <button
            onClick={() => setActiveTab('events')}
            className={`flex items-center gap-2 border-b-2 px-4 py-3 text-sm font-medium transition-colors hover:border-primary/50 hover:bg-primary/10 hover:text-primary ${
              activeTab === 'events'
                ? 'border-primary text-primary'
                : 'border-transparent text-muted-foreground'
            }`}
          >
            <CalendarDays className="size-4" />
            Eventos
          </button>
          {canViewReceipts && (
            <button
              onClick={() => setActiveTab('receipts')}
              className={`flex items-center gap-2 border-b-2 px-4 py-3 text-sm font-medium transition-colors hover:border-primary/50 hover:bg-primary/10 hover:text-primary ${
                activeTab === 'receipts'
                  ? 'border-primary text-primary'
                  : 'border-transparent text-muted-foreground'
              }`}
            >
              <Receipt className="size-4" />
              Recibos
            </button>
          )}
        </nav>
      </div>

      {activeTab === 'info' && (
        <div className="rounded-lg border bg-card p-6">
          <MinisterForm
            formData={formData}
            errors={errors}
            touched={touched}
            onFieldChange={updateField}
            onFieldBlur={markFieldTouched}
            onSubmit={handleSubmit}
            onCancel={handleCancel}
            districts={districts}
            regions={regions}
            degrees={degrees}
            photoPreview={photoPreview}
            onPhotoSelect={handlePhotoSelect}
            shouldDeletePhoto={shouldDeletePhoto}
            isSubmitting={isSubmitting}
            isReadOnly={!canEdit}
            existingMinister={minister}
            hasFormChanged={hasFormChanged}
            onStatusToggle={canInactivate ? handleStatusToggle : undefined}
            onDegreeChangeRequest={canChangeDegree ? () => setShowDegreeChangeDialog(true) : undefined}
            onTransferRequest={canEdit ? () => setShowTransferDialog(true) : undefined}
          />
        </div>
      )}

      {activeTab === 'history' && (
        <div className="rounded-lg border bg-card p-6">
          <h2 className="mb-4 text-base font-semibold">Historial de Estado</h2>
          <MinisterHistoryTimeline key={historyKey} ministerId={minister.id} />
        </div>
      )}

      {activeTab === 'events' && (
        <div className="rounded-lg border bg-card p-6">
          <h2 className="mb-4 text-base font-semibold">Eventos</h2>
          <MinisterEventsTab ministerId={minister.id} />
        </div>
      )}

      {activeTab === 'receipts' && canViewReceipts && (
        <div className="rounded-lg border bg-card p-6">
          <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
            <h2 className="text-base font-semibold">Recibos emitidos</h2>
            {canIssueReceipt && (
              <Button size="sm" onClick={() => setShowAffiliationReceipt(true)}>
                <Receipt className="mr-1.5 size-4" />
                Emitir recibo de afiliación
              </Button>
            )}
          </div>
          <MinisterReceiptsTab ministerId={minister.id} />
        </div>
      )}

      {showAffiliationReceipt && (
        <AffiliationReceiptFlow
          minister={{
            id: minister.id,
            adb_code: minister.adb_code,
            full_name: minister.full_name,
            dni: minister.dni,
            created_at: minister.created_at,
          }}
          districtName={minister.district?.name}
          regionName={minister.region?.name}
          skipPrompt
          onClose={() => setShowAffiliationReceipt(false)}
        />
      )}

      {/* Degree Change Dialog */}
      <DegreeChangeDialog
        open={showDegreeChangeDialog}
        currentDegreeName={minister.degree?.name ?? null}
        degrees={degrees}
        onConfirm={handleDegreeChangeConfirm}
        onCancel={() => setShowDegreeChangeDialog(false)}
      />

      {/* Transfer Dialog */}
      <TransferDialog
        open={showTransferDialog}
        currentDistrictName={minister.district?.name ?? null}
        currentRegionName={minister.region?.name ?? null}
        districts={districts}
        regions={regions}
        onConfirm={handleTransferConfirm}
        onCancel={() => setShowTransferDialog(false)}
      />

      {/* Status Change Dialogs */}
      <InactivateMinisterDialog
        open={showInactivateDialog}
        ministerName={minister.full_name ?? ""}
        reasons={inactiveReasons}
        onConfirm={handleInactivate}
        onCancel={() => setShowInactivateDialog(false)}
      />
      <ReactivateMinisterDialog
        open={showReactivateDialog}
        ministerName={minister.full_name ?? ""}
        onConfirm={handleReactivate}
        onCancel={() => setShowReactivateDialog(false)}
      />

      {/* Cancel Confirmation Dialog */}
      <AlertDialog open={showCancelDialog} onOpenChange={setShowCancelDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Cancelar cambios?</AlertDialogTitle>
            <AlertDialogDescription>
              Los cambios que realizó no se guardarán.
              ¿Está seguro de que desea salir sin guardar?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Continuar editando</AlertDialogCancel>
            <AlertDialogAction onClick={handleConfirmCancel} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
              Descartar cambios
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
